import * as Tooltip from '@radix-ui/react-tooltip';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { extendTrail, readTrail, type TrailStep } from './logic.js';

/**
 * Interconnexions entre modules (docs/ux/composants.md § Interconnexions) : lien d'entité
 * avec bulle de résumé et compteurs, bloc « Lié à » d'une fiche. Le chemin d'arrivée est
 * transmis dans l'état de navigation (`{ trail }`) : la coque l'affiche dans le fil
 * d'Ariane de la fiche ouverte.
 */

export interface EntityCount {
  label: string;
  value: number;
  to?: string | undefined;
}

/**
 * Lien d'entité (personne, activité, organisme, demande, lot, véhicule…) : le nom cliquable
 * ouvre la fiche, sans rechargement ; au survol ou au focus, une bulle résume l'entité
 * (type, résumé, compteurs). `showCounts` écrit aussi les compteurs à côté du nom.
 */
export function EntityLink({
  to,
  label,
  kind,
  icon: Icon,
  summary,
  counts,
  showCounts = false,
}: {
  to: string;
  label: string;
  /** Type d'entité affiché dans la bulle (« Activité », « Organisme »…). */
  kind?: string | undefined;
  icon?: LucideIcon | undefined;
  summary?: ReactNode;
  counts?: readonly EntityCount[] | undefined;
  showCounts?: boolean;
}): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const link = (
    <Link
      to={to}
      className="o-entity"
      onClick={(event) => {
        // Clic ordinaire seulement (Ctrl/⌘ ou clic du milieu : nouvel onglet du navigateur).
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return;
        // Chemin calculé au clic : titre réel de la page quittée.
        const heading = document.querySelector('main h1')?.textContent.trim() ?? '';
        if (heading === '') return;
        event.preventDefault();
        const here: TrailStep = { label: heading, to: `${location.pathname}${location.search}` };
        void navigate(to, { state: { trail: extendTrail(readTrail(location.state), here) } });
      }}
    >
      {Icon !== undefined && <Icon aria-hidden="true" size={14} />}
      <span className="o-entity__label">{label}</span>
    </Link>
  );
  const hasTip = kind !== undefined || summary !== undefined || (counts?.length ?? 0) > 0;
  return (
    <span className="o-entity-wrap">
      {hasTip ? (
        <Tooltip.Provider delayDuration={350}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>{link}</Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="o-popover o-entity-tip"
                sideOffset={6}
                collisionPadding={16}
              >
                {kind !== undefined && <span className="o-entity-tip__kind">{kind}</span>}
                <strong className="o-entity-tip__title">{label}</strong>
                {summary !== undefined && <span className="o-entity-tip__summary">{summary}</span>}
                {counts !== undefined && counts.length > 0 && (
                  <span className="o-entity-tip__counts">
                    {counts.map((c) => (
                      <span key={c.label}>
                        <strong>{c.value}</strong> {c.label}
                      </span>
                    ))}
                  </span>
                )}
                <Tooltip.Arrow className="o-popover__arrow" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      ) : (
        link
      )}
      {showCounts &&
        counts?.map((c) =>
          c.to === undefined ? (
            <span key={c.label} className="o-entity__count">
              {c.value} {c.label}
            </span>
          ) : (
            <Link key={c.label} to={c.to} className="o-entity__count">
              {c.value} {c.label}
            </Link>
          ),
        )}
    </span>
  );
}

export interface RelatedItem {
  to: string;
  label: string;
  detail?: ReactNode;
  summary?: ReactNode;
  kind?: string | undefined;
}

export interface RelatedGroup {
  /** Type d'entités liées (« Activités », « Demandes »…). */
  label: string;
  icon?: LucideIcon | undefined;
  /** Total réel (peut dépasser le nombre d'éléments montrés). */
  count?: number | undefined;
  items: readonly RelatedItem[];
  /** Lien « Tout voir » (liste filtrée sur l'entité courante). */
  more?: { to: string; label?: string } | undefined;
  /** Texte affiché quand rien n'est lié (sans lui, le groupe vide est masqué). */
  empty?: string | undefined;
  /**
   * Groupe-lien : sans éléments détaillés, la ligne entière ouvre la liste filtrée (ex.
   * « Emprunts de matériel 3 › »), avec `hint` en précision et `tone` pour le compteur.
   */
  to?: string | undefined;
  hint?: string | undefined;
  tone?: 'warning' | 'critical' | undefined;
}

/**
 * Bloc « Lié à » d'une fiche : les entités des autres modules liées à celle-ci, par type,
 * avec leur nombre, chacune ouvrable par un lien d'entité (chemin d'arrivée gardé).
 * Compact : `limit` éléments par type (5), puis « Tout voir ». Rien si aucun lien.
 */
export function Related({
  groups,
  title = 'Lié à',
  limit = 5,
}: {
  groups: readonly RelatedGroup[];
  title?: string;
  limit?: number;
}): ReactElement | null {
  const shown = groups.filter(
    (g) => g.items.length > 0 || g.empty !== undefined || g.to !== undefined,
  );
  if (shown.length === 0) return null;
  return (
    <section className="o-related" aria-label={title}>
      <h2 className="o-related__title">{title}</h2>
      {shown.map((g) => {
        const Icon = g.icon;
        if (g.items.length === 0 && g.to !== undefined)
          return (
            <div key={`${g.label}-${g.to}`} className="o-related__group">
              <Link to={g.to} className="o-related__link">
                {Icon !== undefined && <Icon aria-hidden="true" size={16} />}
                <span className="o-related__link-label">
                  {g.label}
                  {g.hint !== undefined && <span className="o-hint">{g.hint}</span>}
                </span>
                {g.count !== undefined && (
                  <span className={g.tone === undefined ? 'o-count' : `o-count o-count--${g.tone}`}>
                    {g.count}
                  </span>
                )}
                <ChevronRight aria-hidden="true" size={16} className="o-related__chevron" />
              </Link>
            </div>
          );
        return (
          <div key={g.label} className="o-related__group">
            <h3 className="o-related__group-title">
              {Icon !== undefined && <Icon aria-hidden="true" size={16} />}
              {g.label}
              <span className="o-count">{g.count ?? g.items.length}</span>
            </h3>
            {g.items.length === 0 ? (
              <p className="o-hint">{g.empty}</p>
            ) : (
              <ul className="o-related__list">
                {g.items.slice(0, limit).map((i) => (
                  <li key={i.to}>
                    <EntityLink to={i.to} label={i.label} kind={i.kind} summary={i.summary} />
                    {i.detail !== undefined && <span className="o-hint">{i.detail}</span>}
                  </li>
                ))}
              </ul>
            )}
            {g.more !== undefined && (
              <Link to={g.more.to} className="o-related__more">
                {g.more.label ?? 'Tout voir'}
              </Link>
            )}
          </div>
        );
      })}
    </section>
  );
}
